import type { IPhaseMappingData, IAtomicMovement } from '@app/queries/optimus/optimus-config'
import type { ITmsPhaseSettings, ITmsTimeOfDay } from '@app/queries/optimus/optimus-config'
import type { IPhaseMappingSignalData } from '../phase-mapping'
import { MOVEMENT_TYPE_ENUM } from '../phase-mapping'

interface IAtomicMovementsWithAssignments {
	phase_numbers: number[]
	atomic_movement: IAtomicMovement
}

export const validatePhaseMappings = (
	signal_specs: IPhaseMappingSignalData | undefined,
	phase_plans: ITmsPhaseSettings[],
	signal_phase_mappings: IPhaseMappingData[],
	time_of_day_schedules: ITmsTimeOfDay[],
): { errors: Record<string, string>; warnings: Record<string, string> } => {
	const errors: Record<string, string> = {}
	const warnings: Record<string, string> = {}

	if (!signal_specs) return { errors, warnings }

	const edge_connections = signal_specs.edge_connections ?? []

	for (const phase_mapping of signal_phase_mappings) {
		const existing_phase_plan = phase_plans.find((pp) => pp.phase_settings_id === phase_mapping.phase_settings_id)
		const is_phase_plan_used = time_of_day_schedules.find(
			(tod) => tod.phase_settings_id === phase_mapping.phase_settings_id,
		)

		if (!is_phase_plan_used && !phase_mapping.phase_mapping.length) continue

		if (!existing_phase_plan) {
			const key = `${phase_mapping.phase_settings_id}_dangling_phase_mapping`
			warnings[key] =
				`Phase Mapping ${phase_mapping.atlas_phase_mapping_id} linked to invalid Phase Plan ${phase_mapping.phase_settings_id}. Please contact your administrator for assistance.`
			continue
		}

		if (!existing_phase_plan.phase_settings_data) {
			const phase_plan_name = existing_phase_plan.phase_settings_external_id ?? '-'
			const key = `${phase_mapping.phase_settings_id}_phase_plan_data_error`
			errors[key] = `Phase Plan ${phase_plan_name}: Invalid data. Please contact your administrator for assistance.`
			continue
		}

		const phase_plan_name = existing_phase_plan.phase_settings_external_id ?? '-'

		const enabled_phase_numbers = Object.entries(existing_phase_plan.phase_settings_data)
			.filter(([, s]) => s.enabled && !s.pedestrianPhase && !s.dummyPhase)
			.map(([n]) => parseInt(n, 10))
			.sort((a, b) => a - b)

		// Phases with only overlap movements are excluded — treat them as unmapped
		const mapped_phase_numbers = phase_mapping.phase_mapping
			.filter((p) => p.atomic_movements.some((am) => am.movement_type !== MOVEMENT_TYPE_ENUM.overlap))
			.map((p) => p.number)

		const only_in_plan = enabled_phase_numbers.filter((n) => !mapped_phase_numbers.includes(n))
		const only_in_mapping = mapped_phase_numbers.filter((n) => !enabled_phase_numbers.includes(n))

		if (only_in_plan.length) {
			errors[`${phase_mapping.phase_settings_id}_phases_in_phase_plan_but_not_phase_mapping`] =
				`Phase Plan ${phase_plan_name}: Phases [${only_in_plan}] in Phase Plan do not exist in Phase Mapping.`
		}
		if (only_in_mapping.length) {
			errors[`${phase_mapping.phase_settings_id}_phases_in_phase_mapping_but_not_phase_plan`] =
				`Phase Plan ${phase_plan_name}: Phases [${only_in_mapping}] in Phase Mapping do not exist in Phase Plan.`
		}

		// Flatten all non-overlap atomic movements with their phase number for duplicate detection
		const flat_movements = phase_mapping.phase_mapping
			.flatMap((p) => p.atomic_movements.map((am) => ({ ...p, ...am })))
			.filter((am) => am.movement_type !== MOVEMENT_TYPE_ENUM.overlap)

		const seen: IAtomicMovementsWithAssignments[] = []

		for (const am of flat_movements) {
			const am_key = `${am.edge_connection_id}_${am.priority}`
			if (seen.find((s) => `${s.atomic_movement.edge_connection_id}_${s.atomic_movement.priority}` === am_key)) continue

			const instances = flat_movements.filter((m) => `${m.edge_connection_id}_${m.priority}` === am_key)
			if (instances.length > 1) {
				seen.push({ phase_numbers: instances.map((m) => m.number), atomic_movement: instances[0] })
			}
		}

		for (const record of seen) {
			const ec_name = edge_connections.find((ec) => ec.id === record.atomic_movement.edge_connection_id)?.name
			const am_label = `${ec_name} (in Phases ${record.phase_numbers})`
			for (const phase_number of record.phase_numbers) {
				const key = `${phase_mapping.phase_settings_id}_${phase_number}_${record.atomic_movement.edge_connection_id}_${record.atomic_movement.priority}_multiple_assignment`
				errors[key] = `Phase Plan ${phase_plan_name}: Movements can only be assigned to one Phase: [${am_label}].`
			}
		}
	}

	return { errors, warnings }
}
